import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/primitives/utils"

const badgeVariants = cva(
  // beacon: a 20px tag in 11px medium text with 12px icons; a quiet label, not a pill.
  "group/badge inline-flex h-5 w-fit shrink-0 items-center gap-1 overflow-hidden rounded-md px-1.5 text-2xs font-medium whitespace-nowrap transition-colors duration-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&_svg]:pointer-events-none [&_svg]:size-3 [&_svg]:shrink-0",
  {
    variants: {
      // beacon: Beacon's badge taxonomy. Neutral is the default and says a fact; brand is teal as
      // text only (the Admin role); each state keeps its token's meaning (warning is a warning,
      // feed-error a fault to notice, destructive a danger). The teal-filled default and
      // `secondary` are gone. Warning and success take their darker text tokens and destructive a
      // lighter wash, so 11px text clears 4.5:1 on its own tint in light.
      variant: {
        neutral: "bg-muted text-muted-foreground",
        outline: "text-muted-foreground ring-1 ring-border ring-inset",
        brand: "bg-primary/10 text-brand-text",
        warning: "bg-warning/12 text-warning-text",
        "feed-error": "bg-feed-error/10 text-feed-error",
        destructive: "bg-destructive/8 text-destructive",
        success: "bg-success/12 text-success-text",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
)

function Badge({
  className,
  variant = "neutral",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant }), className),
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export { Badge, badgeVariants }
